import MessageType from '../../../../enums/MessageType';
import getSessionId from '../../../../getSessionId';
import {TooltipAndOverlayIDs} from '../../../components/detailed-tooltip/TooltipLibrary';
import getGiftcardFormData from '../../../selectors/GiftCardSelectors/getGiftcardFormData';
import makeGetIsDuplicateGiftcardApplied from '../../../selectors/GiftCardSelectors/makeGetIsDuplicateGiftcardApplied';
import {getSettings} from '../../../selectors/selectors';
import bigQueryMetricRecorder from '../../../tracking/bigQueryMetricRecorder';
import Track from '../../../utils/tracking';
import {
  errorUpdatingOrderSummary,
  OrderSummaryUpdateReasons,
  setOrderSummaryUpdating,
  updateOrderSummary,
} from '../../orderSummaryActions';
import {showOverlay} from '../../overlay-actions';
import clearGiftcardForm from './clearGiftcardForm';
import setGiftcardMessage from './setGiftcardMessage';

const bqTracking = (name, value, errors = []) => {
  try {
    bigQueryMetricRecorder('Giftcard', [{name, value}], errors);
  } catch (error) {
    Track.track(error);
  }
};

export function applyGiftcard(isDiggecard = false) {
  return (dispatch, getState) => {
    const isDuplicateGiftcard = !!makeGetIsDuplicateGiftcardApplied(isDiggecard)(getState());
    
    if (isDuplicateGiftcard) {
      dispatch(setGiftcardMessage({messageTranslationKey: isDiggecard ? 'diggecard.errors.duplicate' : 'giftcard.errors.duplicate', messageType: MessageType.ERROR}));
      bqTracking('apply', 'FAILED', [
        {
          type: 'duplicate card applied',
          label: 'Failed to apply giftcard as the card number and pin are already applied',
        },
      ]);
      return;
    }
    dispatch(setOrderSummaryUpdating(OrderSummaryUpdateReasons.APPLYING_GIFTCARD));
    bqTracking('apply', 'PENDING');
    
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      sessionId: getSessionId(),
      'x-thg-client': 'checkout-web-js',
    };
  
    const {giftcardNumber, giftcardPin} = getGiftcardFormData(getState());
    let giftcardFormData = {cardNumber: giftcardNumber, cardPin: giftcardPin};
    
    if (isDiggecard) {
      giftcardFormData = {...giftcardFormData, paymentMethod: 'DIGGECARD'};
    }
    
    return fetch(
      '/checkout-api/v2/giftcard/apply', {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin', // To automatically include cookies with the request
        headers,
        body: JSON.stringify(giftcardFormData),
      },
    ).then((response) => {
      if (response.ok) {
        dispatch(setGiftcardMessage(null));
        response.json()
          .then((orderSummary) => {
            if (isDiggecard && orderSummary.giftCardValidationMessage) {
              if (orderSummary.giftCardValidationMessage === 'EXPIRED') {
                Track.track('Received error response from giftcard/apply endpoint for expired giftcard');
                dispatch(setGiftcardMessage({
                  messageTranslationKey: 'diggecard.giftcard.errors.expired',
                  messageType: MessageType.ERROR,
                }));
                dispatch(errorUpdatingOrderSummary());
                bqTracking('apply', 'FAILED', [
                  {
                    type: 'expired card applied',
                    label: 'Failed to apply giftcard as card expired',
                  },
                ]);
              } else if (orderSummary.giftCardValidationMessage === 'CURRENCY_MISMATCH') {
                dispatch(setGiftcardMessage({
                  messageTranslationKey: 'diggecard.giftcard.errors.currency.mismatch',
                  messageType: MessageType.ERROR,
                }));
                dispatch(errorUpdatingOrderSummary());
                bqTracking('apply', 'FAILED', [
                  {
                    type: 'Currency mismatch for card applied',
                    label: 'Failed to apply giftcard as card had currency mismatch with order',
                  },
                ]);
              } else if (orderSummary.giftCardValidationMessage === 'USED') {
                dispatch(setGiftcardMessage({
                  messageTranslationKey: 'diggecard.giftcard.errors.zero.balance',
                  messageType: MessageType.ERROR,
                }));
                dispatch(errorUpdatingOrderSummary());
                bqTracking('apply', 'FAILED', [
                  {
                    type: 'Zero balance card applied',
                    label: 'Failed to apply giftcard as card had zero balance',
                  },
                ]);
              } else if (orderSummary.giftCardValidationMessage === 'ERROR') {
                dispatch(setGiftcardMessage({
                  messageTranslationKey: 'diggecard.giftcard.errors.apply',
                  messageType: MessageType.ERROR,
                }));
                dispatch(errorUpdatingOrderSummary());
                bqTracking('apply', 'FAILED', [
                  {
                    type: 'error for card applied',
                    label: 'Failed to apply giftcard as card error occurred',
                  },
                ]);
              }
              dispatch(errorUpdatingOrderSummary());
            } else {
              dispatch(updateOrderSummary(orderSummary));
              // TODO: Error handling (chc-802)
              dispatch(clearGiftcardForm());
              bqTracking('apply', 'SUCCESS');
            }
          })
          .catch((error) => {
            Track.track('Failed to parse response from giftcard/apply endpoint');
            dispatch(setGiftcardMessage({
              messageTranslationKey: 'giftcard.errors.apply',
              messageType: MessageType.ERROR,
            }));
            dispatch(errorUpdatingOrderSummary());
            bqTracking('apply', 'FAILED', [{
              type: error,
              label: 'Failed to parse response from giftcard/apply endpoint',
            }]);
          });
      } else if (response.status === 410) {
        Track.track('Received 410 status response from giftcard/apply endpoint for zero balance giftcard');
        dispatch(setGiftcardMessage({messageTranslationKey: 'giftcard.errors.zero.balance', messageType: MessageType.ERROR}));
        dispatch(errorUpdatingOrderSummary());
      } else {
        Track.track('Failed to get ok response from giftcard/apply endpoint');
        dispatch(setGiftcardMessage({messageTranslationKey: 'giftcard.errors.apply', messageType: MessageType.ERROR}));
        dispatch(errorUpdatingOrderSummary());
        bqTracking('apply', 'FAILED', [{type: 'Non ok response', label: 'Failed to get ok response from giftcard/apply endpoint'}]);
      }
    })
      .catch((error) => {
        Track.track('Failed to get orderSummary response from giftcard/apply endpoint', error);
        dispatch(setGiftcardMessage({messageTranslationKey: 'giftcard.errors.apply', messageType: MessageType.ERROR}));
        dispatch(errorUpdatingOrderSummary());
        bqTracking('apply', 'FAILED', [{type: error, label: 'Failed to get orderSummary response from giftcard/apply endpoint'}]);
      });
  };
}

export const callAPIAndRemoveGiftcard = cardUUID => (dispatch) => {
  dispatch(setOrderSummaryUpdating(OrderSummaryUpdateReasons.REMOVING_GIFTCARD));
  bqTracking('remove', 'PENDING');
  
  const headers = {
    'Content-Type': 'application/json',
    sessionId: getSessionId(),
    'x-thg-client': 'checkout-web-js',
  };
  
  return fetch(
    `/checkout-api/v2/giftcard/remove/${cardUUID}`, {
      method: 'DELETE',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin', // To automatically include cookies with the request
      headers,
    },
  ).then((response) => {
    if (response.ok) {
      dispatch(setGiftcardMessage(null));
      response.json()
        .then((orderSummary) => {
          dispatch(updateOrderSummary(orderSummary));
          dispatch(clearGiftcardForm());
          bqTracking('remove', 'SUCCESS');
        })
        .catch((error) => {
          Track.track('Failed to parse response from giftcard/remove endpoint');
          dispatch(setGiftcardMessage({messageTranslationKey: 'giftcard.errors.remove', messageType: MessageType.ERROR}));
          dispatch(errorUpdatingOrderSummary());
          bqTracking('remove', 'FAILED', [{type: error, label: 'Failed to parse response from giftcard/remove endpoint'}]);
        });
    } else {
      Track.track('Failed to get ok response from giftcard/remove endpoint');
      dispatch(setGiftcardMessage({messageTranslationKey: 'giftcard.errors.remove', messageType: MessageType.ERROR}));
      dispatch(errorUpdatingOrderSummary());
      bqTracking('remove', 'FAILED', [{type: 'non ok response', label: 'Failed to get ok response from giftcard/remove endpoint'}]);
    }
  })
    .catch((error) => {
      Track.track('Failed to get orderSummary response from giftcard/remove endpoint', error);
      dispatch(setGiftcardMessage({messageTranslationKey: 'giftcard.errors.remove', messageType: MessageType.ERROR}));
      dispatch(errorUpdatingOrderSummary());
      bqTracking('remove', 'FAILED', [{type: error, label: 'Failed to get orderSummary response from giftcard/remove endpoint'}]);
    });
};

export const removeGiftcard = cardUUID => (dispatch, getState) => {
  if (getSettings(getState())?.should_show_giftcard_remove_overlay) {
    dispatch(showOverlay(TooltipAndOverlayIDs.REMOVE_GIFTCARD, {cardUUID}));
  } else {
    dispatch(callAPIAndRemoveGiftcard(cardUUID));
  }
};
